import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { useState, useEffect } from "react";
import { useSearchParams } from "next/navigation";
import { getUnicornSectorAnalytics } from "@/services/api/UnicornTracker";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Info } from "@phosphor-icons/react";
import CustomTooltip from "@/components/CustomTooltip";

export default function UnicornBySector({
  unicornStats,
  regionData,
  countryData,
}) {
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [regionName, setRegionName] = useState("");

  const searchParams = useSearchParams();
  const selectedRegionId = searchParams.get("unicorn_regions");
  const selectedCountryId = searchParams.get("country");
  const sectors = searchParams.get("sectors");
  const stages = searchParams.get("stages");
  const valuationCondition = searchParams.get("valuation_condition");
  const valuationAmount = searchParams.get("valuation_amount");
  const yearFoundedCondition = searchParams.get("year_founded_condition");
  const yearFoundedYear = searchParams.get("year_founded_year");
  const timeToUnicornMin = searchParams.get("min_time_to_unicorn");
  const timeToUnicornMax = searchParams.get("max_time_to_unicorn");
  const unicornStatus = searchParams.get("unicorn_status");
  // Fetch unicorn sector distribution data
  useEffect(() => {
    async function fetchSectorData() {
      try {
        setLoading(true);

        // Build parameters for sector analytics API
        const params = {
          limit: 5,
          sort_by: "count",
          sort_order: "desc",
        };

        // Add region filter if selected
        if (selectedRegionId) {
          params.unicorn_regions = selectedRegionId;
        }

        // Add country filter if selected
        if (selectedCountryId) {
          params.countries = selectedCountryId;
        }

        // Add additional filters
        if (sectors) {
          params.sectors = sectors;
        }
        if (stages) {
          params.stages = stages;
        }
        if (valuationCondition && valuationAmount) {
          params.valuation_condition = valuationCondition;
          params.valuation_amount = valuationAmount;
        }
        if (yearFoundedCondition && yearFoundedYear) {
          params.year_founded_condition = yearFoundedCondition;
          params.year_founded_year = yearFoundedYear;
        }
        if (timeToUnicornMin) {
          params.min_time_to_unicorn = timeToUnicornMin;
        }
        if (timeToUnicornMax) {
          params.max_time_to_unicorn = timeToUnicornMax;
        }

        const statuses = unicornStatus
          ? unicornStatus.split(",").filter(Boolean)
          : ["current", "fallen", "alumni"];
        params.unicorn_status = statuses.join(",");
        if (statuses.includes("fallen")) params.include_lost_unicorn = 1;
        if (statuses.includes("alumni")) params.include_alumni = 1;
        if (!statuses.includes("current")) params.exclude_current_unicorn = 1;

        // Get sector analytics data
        const response = await getUnicornSectorAnalytics({ params });
        const sectorData = response.data?.data?.sectors || [];

        // Convert to chart format
        const chartData = sectorData.map((sector) => ({
          name: sector.name,
          y: sector.unicorn_count,
          percentage: sector.percentage_of_total,
        }));

        setChartData(chartData);
      } catch (error) {
        console.error("Error fetching sector data:", error);
      } finally {
        setLoading(false);
      }
    }

    fetchSectorData();
  }, [
    selectedRegionId,
    selectedCountryId,
    sectors,
    stages,
    valuationCondition,
    valuationAmount,
    yearFoundedCondition,
    yearFoundedYear,
    timeToUnicornMin,
    timeToUnicornMax,
    unicornStatus,
  ]);

  // Set region name for display
  useEffect(() => {
    if (countryData?.name) {
      setRegionName(countryData.name);
    } else if (regionData?.name) {
      setRegionName(regionData.name);
    } else {
      setRegionName("");
    }
  }, [regionData, countryData]);

  const chartColors = ["#00C49F", "#7E57C2", "#FFB300", "#29B6F6", "#FF7043"];

  const options = {
    chart: {
      backgroundColor: "transparent",
      type: "bar",
      height: 260,
      spacing: [6, 24, 6, 10],
      style: {
        fontFamily: "inherit",
      },
    },
    title: { text: null },
    xAxis: {
      categories: chartData.map((d) => d.name),
      lineWidth: 0,
      gridLineWidth: 0,
      reversed: true, // Ensure top items appear at the top
      labels: {
        formatter: function () {
          const value = String(this.value || "");
          return value.length > 16 ? `${value.slice(0, 16)}…` : value;
        },
        style: {
          color: "rgba(255,255,255,0.7)",
          fontSize: "12px",
          fontWeight: "500",
        },
      },
    },
    yAxis: {
      title: { text: null },
      gridLineWidth: 0,
      labels: { enabled: false },
    },
    tooltip: {
      backgroundColor: "#161617",
      borderColor: "#333",
      style: { color: "#fff" },
      headerFormat: '<span style="font-size: 10px">{point.key}</span><br/>',
      pointFormat: "Count: <b>{point.y}</b>",
    },
    plotOptions: {
      bar: {
        borderRadius: 4,
        colorByPoint: true,
        colors: chartColors,
        borderWidth: 0,
        dataLabels: {
          enabled: true,
          align: "left",
          inside: false,
          x: 6,
          format: "{y}",
          crop: false,
          overflow: "allow",
          style: {
            color: "rgba(255,255,255,0.9)",
            textOutline: "none",
            fontSize: "12px",
            fontWeight: "bold",
          },
        },
      },
      series: {
        states: {
          hover: {
            enabled: true,
            brightness: 0.1,
          },
        },
      },
    },
    legend: { enabled: false },
    credits: { enabled: false },
    series: [
      {
        name: "Sectors",
        data: chartData,
      },
    ],
  };

  // Determine empty state message
  const getEmptyStateMessage = () => {
    if (unicornStats?.total_unicorn === 0) {
      return "No unicorns found for this selection";
    }
    return "No sector data available";
  };

  if (loading) {
    return (
      <div className='flex items-center justify-center h-[260px]'>
        <div className='w-8 h-8 border-4 rounded-full border-white/20 border-t-white/80 animate-spin'></div>
      </div>
    );
  }

  const isEmpty = chartData.length === 0;

  return (
    <>
      <div className='flex items-center justify-between gap-1.5 mb-4'>
        <h3 className='text-base font-medium sm:text-sm'>Top Sectors</h3>
        <CustomTooltip
          trigger={
            <span className='-mt-2 -mr-2 text-white/40'>
              <Info className='size-5' />
            </span>
          }
          content="Top five sectors by number of unicorns, for your current filters. Companies tagged with multiple sectors appear in each; counts won't sum to the total above."
        />
      </div>

      {isEmpty ? (
        <div className='flex flex-col items-center justify-center h-[160px] mb-4 text-center border border-dashed rounded-xl border-white/10'>
          <p className='text-sm opacity-40'>{getEmptyStateMessage()}</p>
        </div>
      ) : (
        <HighchartsReact highcharts={Highcharts} options={options} />
      )}
    </>
  );
}
